/**
 * This custom hook provides the functionality to retrieve users to be shown in the UI.
 */

import {useContext, useEffect, useState} from "react";
import {FirebaseAuthContext} from "../../../../commons/firebase/FirebaseAuthProvider";
import {useSnackbar} from "notistack";
import {useIntl} from "react-intl";

const useListChallengedDecisions = (scoredSceneKey) => {

    // State storage of the challenged decisions of the given scored scene
    const [challengedDecisions, setChallengedDecisions] = useState([])

    // Loading indication
    const [loading, setLoading] = useState(true)

    const {signedInUser} = useContext(FirebaseAuthContext);

    const {enqueueSnackbar} = useSnackbar();


    // Internationalization for simple strings
    const intl = useIntl();

    useEffect(() => {
        fetchChallengedDecisions();
    }, [])

    // Retrieving the already existing videos
    const fetchChallengedDecisions = async () => {
        const idToken = await signedInUser.getIdToken()

        return fetch('/reg-main/challengedScoredScenes/' + scoredSceneKey + '/challengedDecisions?idToken=' + idToken)
            .then(async (data) => {
                // Throw an error including the textual status of the HTTP response code (no detailed message necessary)
                if (data.status !== 200) {
                    throw new Error((await data.json()).status)
                }

                const challengedDecisionsJson = await data.json();
                setChallengedDecisions(challengedDecisionsJson)

                return challengedDecisionsJson
            })
            .catch((error) => {
                enqueueSnackbar(
                    intl.formatMessage({
                        id: 'app.challengedDecision.messages.challengedDecisions.list.error',
                        defaultMessage: 'Something went wrong while listing the challenged decisions: {error}'
                    }, {error: error.message}), {
                        variant: 'error',
                        preventDuplicate: true,
                        autoHideDuration: 5000
                    });
            })
            .finally(() => {
                setLoading(false)
            })
    }

    // Closing a challenged decision
    const toggleChallengedDecisionStatus = async (key) => {
        const idToken = await signedInUser.getIdToken()

        return fetch('/reg-main/challengedDecisions/' + key + '/toggleStatus?idToken=' + idToken, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
                idToken: idToken
            })
        })
            .then(async (data) => {
                // Throw an error including the textual status of the HTTP response code (no detailed message necessary)
                if (data.status !== 200) {
                    throw new Error((await data.json()).status)
                }

                // Refresh list to reflect the status updated in the UI
                await fetchChallengedDecisions()

                enqueueSnackbar(
                    intl.formatMessage({
                        id: 'app.challengedDecision.messages.toggleStatus.success',
                        defaultMessage: 'Status updated'
                    }), {
                        variant: 'success',
                        preventDuplicate: true,
                        autoHideDuration: 3000
                    });

                return data.json()
            })
            .catch((error) => {
                enqueueSnackbar(
                    intl.formatMessage({
                        id: 'app.challengedDecision.messages.toggleStatus.error',
                        defaultMessage: 'Something went wrong while setting status of challenged decisions: {error}'
                    }, {error: error.message}), {
                        variant: 'error',
                        preventDuplicate: true,
                        autoHideDuration: 5000
                    });
            })
    }

    return {challengedDecisions, loading, toggleChallengedDecisionStatus}
}
export default useListChallengedDecisions;